import FormBox from '../../common/FormBox/FormBox';
import { useEffect, useState } from 'react';
import ToggleButtonGroup from '../../common/ToggleButtonGroup/ToggleButtonGroup';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { FaTrash } from 'react-icons/fa';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from 'api';
import { formatNumber } from 'utils/formatting';
import ReactGA from 'react-ga4';
import AddEntryFooter from '../AddEntryFooter';
import * as S from '../Forms.styled';
import { useAuth } from 'contexts/ProtectedRoute';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { isSalesPersonRole } from 'data/constants/roles';
import { useTranslation } from 'react-i18next';
import { formatDateToYMD, isFullDateFormat, parseStoredDate } from 'utils/fullDateFormat';
import { fetchOptionsReturn } from './utils';
import { startOfDay } from 'date-fns';

interface Entry {
    start: Date | null;
    end: Date | null;
    current: boolean | undefined;
    country: string;
    region: string;
    city: string;
    percent_ownership: number | null;
    type: string;
    number_employees: number | null;
    annual_sales: number | null;
    net_profit: number | null;
    net_company_value: number | null;
}

const initEntry: Entry = {
    start: null,
    end: null,
    current: undefined,
    country: '',
    region: '',
    city: '',
    percent_ownership: 0,
    type: '',
    number_employees: 0,
    annual_sales: 0,
    net_profit: 0,
    net_company_value: 0,
};

interface Msg {
    start: string;
    end: string;
    current: string;
    country: string;
    region: string;
    city: string;
    percent_ownership: string;
    type: string;
    number_employees: string;
    annual_sales: string;
    net_profit: string;
    net_company_value: string;
}

const initMsg: Msg = {
    start: '',
    end: '',
    current: '',
    country: '',
    region: '',
    city: '',
    percent_ownership: '',
    type: '',
    number_employees: '',
    annual_sales: '',
    net_profit: '',
    net_company_value: '',
};

export default function BusinessOwnership() {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const [data, setData] = useState<Entry[]>([]);
    const [validationMsg, setValidationMsg] = useState<Msg[]>([]);
    const [hasBusiness, setHasBusiness] = useState<string>(t('no', { lng: language }));

    const [isUserData, setIsUserData] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [options, setOptions] = useState({
        countryOptions: [''],
        businessSectorOptions: [''],
        regionOptions: {} as { [key: string]: string[] },
    });
    useEffect(() => {
        if (isUserData) {
            setIsLoading(false);
        }
    }, [isUserData]);

    const loadData = (data: Entry[]) => {
        loadOptions();
        if (data && data.length | Object.keys(data).length) {
            const tmp = new Array(data.length).fill(initMsg);
            setValidationMsg(tmp);
            setData(data);
            if (data.length > 0) {
                setHasBusiness(t('yes', { lng: language }));
            }
        }
        if (data == null || data) {
            setIsUserData(true);
        } else {
            setIsUserData(false);
        }
    };

    const loadOptions = async () => {
        const [countries, regions, businessSectors] = await Promise.all([
            fetchOptionsReturn(api?.assessment_options.getCountryOptions),
            fetchOptionsReturn(api?.assessment_options.getRegionOptions),
            fetchOptionsReturn(api?.assessment_options.getBusinessSectorOptions),
        ]);

        setOptions({
            countryOptions: countries || [''],
            regionOptions: regions || {},
            businessSectorOptions: businessSectors || [''],
        });
    };

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname + '/Business'}`,
            title: 'BusinessForm',
        });
    }, []);

    const { role } = useAuth();
    const params = useParams();
    const candidate_id = Number(params.id);
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);

    const isSalesPerson = isSalesPersonRole(role);

    useEffect(() => {
        if (isAuthenticated) {
            setIsLoading(true);
            setIsUserData(false);
            setData([]);
            isSalesPerson
                ? api.tableInput.fetchCandidateInputSales(loadData, 'Business', candidate_id)
                : api.tableInput.fetchCandidateInput(loadData, 'Business');
        } else {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, candidate_id]);

    // FIELD VALIDATION
    const validate = async (
        field?: string,
        value?: string,
        valid?: boolean,
        idx?: number,
        section?: string
    ) => {
        let validated = true;
        const temp = [...validationMsg];
        //business
        for (let i = 0; i < data.length; i++) {
            for (const key of Object.keys(initEntry)) {
                const value = data[i][key as keyof Entry];
                let msg = '';

                if (key === 'current' && value === false) continue;
                if (key === 'end' && data[i].current) continue;
                if (
                    (key === 'city' || key === 'region') &&
                    data[i].country !== 'Australia' &&
                    data[i].country !== 'Canada' &&
                    data[i].country !== 'United Kingdom'
                )
                    continue;

                if (!value) {
                    msg = t('requiredField', { lng: language });
                    validated = false;
                }

                temp[i] = { ...temp[i], [key]: msg };
            }
        }
        setValidationMsg(temp);
        return validated;
    };

    const validateSingleField = (idx: number, field: string, value: string | boolean | null) => {
        const temp = [...validationMsg];
        let msg = '';
        if (field === 'current' && value === false) {
            console.log('nothing is needed');
        } else if (!value) {
            msg = t('requiredField', { lng: language });
        }

        temp[idx] = { ...temp[idx], [field]: msg };
        setValidationMsg(temp);
    };

    const handleUpdate = (idx: number, field: string, value: string | boolean | null) => {
        validateSingleField(idx, field, value);
        const temp = [...data];
        if (field === 'country') {
            temp[idx] = { ...data[idx], region: '' };
        }
        temp[idx] = { ...data[idx], [field]: value };
        setData(temp);
    };

    const validateNumber = (idx: number, field: string, value: string | null) => {
        let validated = true;
        const temp = [...validationMsg];
        let msg = '';
        if (!value) {
            msg = t('requiredField', { lng: language });
            validated = false;
        } else if (isNaN(Number(value.replaceAll(',', '')))) {
            msg = t('shouldBeANumber', { lng: language });
            validated = false;
        }
        temp[idx] = { ...temp[idx], [field]: msg };
        setValidationMsg(temp);
        return validated;
    };

    const handleNumberUpdate = (idx: number, field: string, value: string) => {
        // const filteredValue = value.replace(/[^0-9,\.]/g, '');
        if (value.length > 1 && value[0] === '0') value = value.substring(1);
        if (validateNumber(idx, field, value)) {
            value = value ? value : '0';
            const valueWithoutSeparators = value.replaceAll('.', '').split(',').join('');
            const tmp = [...data];
            tmp[idx] = { ...data[idx], [field]: valueWithoutSeparators };
            setData(tmp);
        }
    };

    const handleDateUpdate = (idx: number, field: string, newDate: Date | null) => {
        if (newDate === null) {
            const temp = [...validationMsg];
            temp[idx] = {
                ...temp[idx],
                [field]: t('requiredField', { lng: language }),
            };
            setValidationMsg(temp);
            const tmp = [...data];
            tmp[idx] = { ...tmp[idx], [field]: '' };
            setData(tmp);
            return;
        }
        if (!(newDate instanceof Date) || isNaN(newDate.getTime())) {
            const temp = [...validationMsg];
            temp[idx] = { ...temp[idx], [field]: 'Invalid date format' };
            setValidationMsg(temp);
            return;
        }

        const extractDateFormat = formatDateToYMD(newDate);

        if (!isFullDateFormat(newDate)) {
            // Incomplete year while typing — keep month visible
            const temp = [...validationMsg];
            temp[idx] = { ...temp[idx], [field]: 'Invalid date format' };
            setValidationMsg(temp);
            const tmp = [...data];
            tmp[idx] = { ...tmp[idx], [field]: extractDateFormat };
            setData(tmp);
            return;
        }

        const temp = [...validationMsg];
        temp[idx] = { ...temp[idx], [field]: '' };
        setValidationMsg(temp);
        const tmp = [...data];
        tmp[idx] = { ...tmp[idx], [field]: extractDateFormat };
        if (field === 'start') {
            tmp[idx] = { ...tmp[idx], end: null };
        }
        setData(tmp);
    };

    const handleUpdateHasBusiness = (value: string) => {
        if (value === t('yes', { lng: language })) {
            setHasBusiness(t('yes', { lng: language }));
            setData([initEntry]);
            setValidationMsg([initMsg]);
        } else if (value === t('no', { lng: language })) {
            setHasBusiness(t('no', { lng: language }));
            setData([]);
            setValidationMsg([]);
        }
    };

    const handleAddEntry = () => {
        if (data.length > 10) return;
        const newEntry: Entry = initEntry;

        const temp: Entry[] = [...data, newEntry];
        setData(temp);

        const tempMsg: Msg[] = [...validationMsg, initMsg];
        setValidationMsg(tempMsg);
    };

    const handleDeleteEntry = (idx: number) => {
        const temp: Entry[] = [...data];
        temp.splice(idx, 1);
        setData(temp);
        const tempMsg: Msg[] = [...validationMsg];
        tempMsg.splice(idx, 1);
        setValidationMsg(tempMsg);
    };

    const checkEndDateBeforeStart = (
        endDate: Date | null,
        startDate: Date | null | undefined,
        idx: number
    ) => {
        const normalizedStartDate = startDate ? startOfDay(new Date(startDate)) : undefined;
        const normalizedEndDate = endDate ? startOfDay(endDate) : undefined;

        if (normalizedEndDate && normalizedStartDate && normalizedEndDate < normalizedStartDate) {
            const temp = [...validationMsg];
            temp[idx] = {
                ...temp[idx],
                end: t('endDateBeforeStartDate', { lng: language }),
            };
            setValidationMsg(temp);
            return true;
        }

        return false;
    };

    return (
        <>
            {isLoading ? (
                <S.LoadingContainer>
                    <CircularProgress color="primary" />
                </S.LoadingContainer>
            ) : (
                <FormBox validation={validate} data={[data]} tableNames={['Business']}>
                    <S.Title>{t('businessOwnership', { lng: language })}</S.Title>

                    <div style={{ marginBottom: '2rem' }}>
                        <ToggleButtonGroup
                            customStyle={true}
                            options={[t('yes', { lng: language }), t('no', { lng: language })]}
                            name="have_investment"
                            label={t('doYouOwnBusiness', { lng: language })}
                            value={hasBusiness}
                            onChange={(_: string, value: string) => handleUpdateHasBusiness(value)}
                        />
                    </div>
                    {hasBusiness === t('yes', { lng: language }) &&
                        data.map((item, i) => {
                            return (
                                <S.EntryCard key={`investment-${i}`}>
                                    <div>
                                        <S.SubTitle>
                                            <>{`${t('business', { lng: language })} ${i + 1}`}</>
                                            <S.DeleteButton>
                                                <button
                                                    onClick={() => {
                                                        handleDeleteEntry(i);
                                                    }}
                                                >
                                                    <FaTrash />
                                                </button>
                                            </S.DeleteButton>
                                        </S.SubTitle>
                                    </div>
                                    <S.FlexColumn>
                                        <S.FlexColumn>
                                            <S.GridSection>
                                                <ToggleButtonGroup
                                                    options={[
                                                        t('yes', { lng: language }),
                                                        t('no', { lng: language }),
                                                    ]}
                                                    name="current"
                                                    label={t('current', { lng: language })}
                                                    value={
                                                        data[i].current === undefined
                                                            ? undefined
                                                            : data[i].current
                                                              ? t('yes', { lng: language })
                                                              : t('no', { lng: language })
                                                    }
                                                    onChange={(arg1: string, arg2: string) =>
                                                        handleUpdate(
                                                            i,
                                                            arg1,
                                                            arg2 === t('yes', { lng: language })
                                                        )
                                                    }
                                                    error={validationMsg[i].current}
                                                />
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <DesktopDatePicker
                                                        label={`*${t('startDate', {
                                                            lng: language,
                                                        })}`}
                                                        maxDate={new Date()}
                                                        views={['year', 'month']}
                                                        openTo="year"
                                                        format="MM/yyyy"
                                                        value={parseStoredDate(data[i].start)}
                                                        onChange={(
                                                            date: Date | null,
                                                            _: string | undefined
                                                        ) => {
                                                            handleDateUpdate(i, 'start', date);
                                                        }}
                                                        slotProps={{
                                                            field: { clearable: true },
                                                            textField: {
                                                                error:
                                                                    validationMsg[i].start !== '',
                                                                helperText: validationMsg[i].start,
                                                            },
                                                        }}
                                                    />
                                                </LocalizationProvider>

                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <DesktopDatePicker
                                                        views={['year', 'month']}
                                                        minDate={
                                                            parseStoredDate(data[i].start) ??
                                                            new Date()
                                                        }
                                                        maxDate={new Date()}
                                                        label={`*${t('endDate', {
                                                            lng: language,
                                                        })}`}
                                                        format="MM/yyyy"
                                                        openTo="year"
                                                        disabled={data[i].current}
                                                        value={
                                                            data[i].current
                                                                ? null
                                                                : parseStoredDate(data[i].end)
                                                        }
                                                        onChange={(date: Date | null) => {
                                                            // Commit typed value first so the field stays in sync
                                                            // (early return left partial years e.g. 0201 on screen).
                                                            handleDateUpdate(i, 'end', date);
                                                            if (date && isFullDateFormat(date)) {
                                                                checkEndDateBeforeStart(
                                                                    date,
                                                                    parseStoredDate(data[i].start),
                                                                    i
                                                                );
                                                            }
                                                        }}
                                                        slotProps={{
                                                            field: { clearable: true },
                                                            textField: {
                                                                error: validationMsg[i].end !== '',
                                                                helperText: validationMsg[i].end,
                                                            },
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                                <S.FlexColumn>
                                                    <Autocomplete
                                                        disablePortal
                                                        id="country"
                                                        value={data[i].country}
                                                        options={options.countryOptions || []}
                                                        onChange={(_, value: string | null) => {
                                                            handleUpdate(i, 'country', value);
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={`*${t('country', {
                                                                    lng: language,
                                                                })}`}
                                                                error={Boolean(
                                                                    validationMsg[i].country
                                                                )}
                                                            />
                                                        )}
                                                    />
                                                    <S.ValidationMsg>
                                                        {validationMsg[i].country}
                                                    </S.ValidationMsg>
                                                </S.FlexColumn>

                                                {(data[i].country === 'Australia' ||
                                                    data[i].country === 'Canada' ||
                                                    data[i].country === 'United Kingdom') && (
                                                    <>
                                                        <S.FlexColumn>
                                                            <Autocomplete
                                                                disablePortal
                                                                id="region"
                                                                value={data[i].region}
                                                                options={
                                                                    options.regionOptions[
                                                                        data[i].country as string
                                                                    ] || []
                                                                }
                                                                onChange={(
                                                                    _,
                                                                    value: string | null
                                                                ) =>
                                                                    handleUpdate(i, 'region', value)
                                                                }
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label={`*${t('region', {
                                                                            lng: language,
                                                                        })}`}
                                                                        error={Boolean(
                                                                            validationMsg[i].region
                                                                        )}
                                                                    />
                                                                )}
                                                            />
                                                            <S.ValidationMsg>
                                                                {validationMsg[i].region}
                                                            </S.ValidationMsg>
                                                        </S.FlexColumn>

                                                        <S.TextField
                                                            id="city"
                                                            label={`*${t('city', {
                                                                lng: language,
                                                            })}`}
                                                            type="text"
                                                            value={item.city}
                                                            onChange={(e) => {
                                                                handleUpdate(
                                                                    i,
                                                                    'city',
                                                                    e.target.value
                                                                );
                                                            }}
                                                            error={validationMsg[i].city !== ''}
                                                            helperText={validationMsg[i].city}
                                                        />
                                                    </>
                                                )}

                                                <Autocomplete
                                                    value={item.type}
                                                    id="type"
                                                    options={options.businessSectorOptions}
                                                    onChange={(_, value: string | null) =>
                                                        handleUpdate(i, 'type', value)
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('businessType', {
                                                                lng: language,
                                                            })}`}
                                                            error={Boolean(validationMsg[i].type)}
                                                            helperText={validationMsg[i].type}
                                                        />
                                                    )}
                                                />
                                                <S.TextField
                                                    id="percent_ownership"
                                                    label={`*${t('shareOfCompany', {
                                                        lng: language,
                                                    })}`}
                                                    type="text"
                                                    value={item.percent_ownership}
                                                    onChange={(e) => {
                                                        handleNumberUpdate(
                                                            i,
                                                            'percent_ownership',
                                                            e.target.value
                                                        );
                                                    }}
                                                    error={
                                                        validationMsg[i].percent_ownership !== ''
                                                    }
                                                    helperText={validationMsg[i].percent_ownership}
                                                />
                                                <S.TextField
                                                    id="number_employees"
                                                    label={`*${t('numberOfEmployees', {
                                                        lng: language,
                                                    })}`}
                                                    type="text"
                                                    value={formatNumber(item.number_employees)}
                                                    onChange={(e) => {
                                                        handleNumberUpdate(
                                                            i,
                                                            'number_employees',
                                                            e.target.value
                                                        );
                                                    }}
                                                    error={validationMsg[i].number_employees !== ''}
                                                    helperText={validationMsg[i].number_employees}
                                                />
                                                <S.TextField
                                                    id="annual_sales"
                                                    label={`*${t('annualSales(USD)', {
                                                        lng: language,
                                                    })}`}
                                                    type="text"
                                                    value={formatNumber(item.annual_sales)}
                                                    onChange={(e) => {
                                                        handleNumberUpdate(
                                                            i,
                                                            'annual_sales',
                                                            e.target.value
                                                        );
                                                    }}
                                                    error={validationMsg[i].annual_sales !== ''}
                                                    helperText={validationMsg[i].annual_sales}
                                                />
                                                <S.TextField
                                                    id="net_profit"
                                                    label={`*${t('netProfit(USD)', {
                                                        lng: language,
                                                    })}`}
                                                    type="text"
                                                    value={formatNumber(item.net_profit)}
                                                    onChange={(e) => {
                                                        handleNumberUpdate(
                                                            i,
                                                            'net_profit',
                                                            e.target.value
                                                        );
                                                    }}
                                                    error={validationMsg[i].net_profit !== ''}
                                                    helperText={validationMsg[i].net_profit}
                                                />
                                                <S.TextField
                                                    id="net_company_worth"
                                                    label={`*${t('netCompanyValue(USD)', {
                                                        lng: language,
                                                    })}`}
                                                    type="text"
                                                    value={formatNumber(item.net_company_value)}
                                                    onChange={(e) => {
                                                        handleNumberUpdate(
                                                            i,
                                                            'net_company_value',
                                                            e.target.value
                                                        );
                                                    }}
                                                    error={
                                                        validationMsg[i].net_company_value !== ''
                                                    }
                                                    helperText={validationMsg[i].net_company_value}
                                                />
                                            </S.GridSection>
                                        </S.FlexColumn>
                                    </S.FlexColumn>
                                </S.EntryCard>
                            );
                        })}
                    {hasBusiness === t('yes', { lng: language }) && (
                        <AddEntryFooter
                            title={t('addBusiness', { lng: language })}
                            addLabel={t('addBusiness', { lng: language })}
                            addAnotherLabel={t('addAnotherBusiness', { lng: language })}
                            hasEntries={data.length > 0}
                            onClick={() => handleAddEntry()}
                        />
                    )}
                </FormBox>
            )}
        </>
    );
}
